/**
 * 官网更新日志的编辑器。
 *
 * 版式沿用曲库那一侧：左边一栏列表，右边编辑区，选中态是整块橙黄。
 * 预览用一个很小的 Markdown 子集自己渲染，不引 marked——后台只是给作者
 * 看个大概排版，为这点用途给管理后台加两个依赖不划算。官网那边才用真的解析器。
 */
import { useEffect, useMemo, useState } from 'react';
import { api, type NewsPost } from '../lib/api';
import { formatTime } from '../lib/format';
import { useConfirm } from './ConfirmDialog';

const BLANK: NewsPost = {
  slug: '',
  title: '',
  excerpt: '',
  body: '',
  tag: null,
  state: 'draft',
};

const slugify = (input: string) => input
  .toLowerCase()
  .replace(/['']/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 64);

const escapeHtml = (s: string) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/** 只认标题、列表、粗体、斜体、行内代码和链接，够看清结构就行 */
function miniMarkdown(source: string): string {
  const inline = (text: string) => escapeHtml(text)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');

  const out: string[] = [];
  let list: string[] = [];

  const flush = () => {
    if (list.length) out.push(`<ul>${list.map((li) => `<li>${li}</li>`).join('')}</ul>`);
    list = [];
  };

  for (const raw of source.split('\n')) {
    const line = raw.trim();
    if (!line) { flush(); continue; }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      const level = Math.min(heading[1].length + 1, 5);
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) { list.push(inline(bullet[1])); continue; }

    flush();
    out.push(`<p>${inline(line)}</p>`);
  }
  flush();
  return out.join('');
}

export function NewsEditor({ onError, onToast }: {
  onError: (err: unknown) => void;
  onToast: (message: string, kind?: 'success' | 'error') => void;
}) {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<NewsPost>(BLANK);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const { confirm, dialog } = useConfirm();

  const refresh = async (keep?: string) => {
    try {
      const res = await api.listNews();
      setPosts(res.posts);
      const next = keep ?? selected;
      const found = res.posts.find((p) => p.slug === next);
      if (found) { setSelected(found.slug); setDraft(found); }
    } catch (err) { onError(err); }
  };

  useEffect(() => { void refresh(); }, []);

  const dirty = useMemo(() => {
    if (creating) return draft.title.length > 0 || draft.body.length > 0;
    const original = posts.find((p) => p.slug === selected);
    if (!original) return false;
    return (['title', 'excerpt', 'body', 'tag', 'state', 'slug'] as const)
      .some((key) => (draft[key] ?? '') !== (original[key] ?? ''));
  }, [creating, draft, posts, selected]);

  const open = async (post: NewsPost) => {
    if (dirty && !await confirm({
      title: 'Discard your changes?',
      body: 'This post has edits that have not been saved yet.',
      confirmLabel: 'Discard',
      danger: true,
    })) return;
    setCreating(false);
    setSelected(post.slug);
    setDraft(post);
    setPreview(false);
  };

  const startNew = async () => {
    if (dirty && !await confirm({
      title: 'Discard your changes?',
      body: 'This post has edits that have not been saved yet.',
      confirmLabel: 'Discard',
      danger: true,
    })) return;
    setCreating(true);
    setSelected(null);
    setDraft(BLANK);
    setPreview(false);
  };

  const save = async (state?: NewsPost['state']) => {
    const payload = { ...draft, state: state ?? draft.state };
    // 没手填 slug 就从标题推，推不出来（比如纯中文标题）再让人自己填
    if (!payload.slug) payload.slug = slugify(payload.title);

    setBusy(true);
    try {
      const res = creating
        ? await api.createNews(payload)
        : await api.updateNews(selected!, payload);
      setCreating(false);
      setSelected(res.post.slug);
      setDraft(res.post);
      await refresh(res.post.slug);
      onToast(res.post.state === 'published' ? 'Published to the site' : 'Draft saved', 'success');
    } catch (err) { onError(err); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!await confirm({
      title: `Delete "${draft.title}"?`,
      body: 'It disappears from the site immediately. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    })) return;
    try {
      await api.deleteNews(selected);
      setSelected(null);
      setCreating(false);
      setDraft(BLANK);
      await refresh();
      onToast('Post deleted');
    } catch (err) { onError(err); }
  };

  const editing = creating || selected !== null;

  return (
    <>
      <aside className="sidebar">
        <div className="sidebar-head">
          <h2>Posts</h2>
          <div className="spacer" />
          <button className="tiny" onClick={startNew}>New</button>
        </div>
        <div className="album-scroll">
          {!posts.length && <p className="hint" style={{ padding: '8px 10px' }}>No posts yet.</p>}
          {posts.map((post) => (
            <button
              key={post.slug}
              className={selected === post.slug && !creating ? 'album-item active' : 'album-item'}
              onClick={() => open(post)}
            >
              <div className="album-meta">
                <div className="album-name">{post.title}</div>
                <div className="album-sub">
                  {post.state === 'published'
                    ? formatTime(post.publishedAt ?? '')
                    : 'Draft'}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <main className="main">
        {!editing ? (
          <div className="empty-state">
            <div>
              <p>Pick a post on the left, or write a new one.</p>
            </div>
          </div>
        ) : (
          <div className="news-editor">
            <div className="album-title-row">
              <input
                className="album-title-input"
                style={{ flex: 1 }}
                value={draft.title}
                placeholder="Post title"
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
              <span className={`badge ${draft.state === 'published' ? 'published' : ''}`}>
                {draft.state === 'published' ? 'Live' : 'Draft'}
              </span>
            </div>

            <div className="news-meta">
              <div className="field">
                <label>URL slug</label>
                <input
                  value={draft.slug}
                  placeholder={slugify(draft.title) || 'my-first-post'}
                  onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                />
                <p className="hint">mininecozyroom.com/news/{draft.slug || slugify(draft.title) || '…'}</p>
              </div>
              <div className="field">
                <label>Tag</label>
                <input
                  value={draft.tag ?? ''}
                  placeholder="Update, Fix, Behind the scenes…"
                  onChange={(e) => setDraft({ ...draft, tag: e.target.value || null })}
                />
              </div>
            </div>

            <div className="field">
              <label>Excerpt</label>
              <input
                value={draft.excerpt}
                maxLength={300}
                placeholder="One or two lines shown on the cards"
                onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              />
            </div>

            <div className="field">
              <div className="news-body-head">
                <label style={{ margin: 0 }}>Body</label>
                <div className="spacer" />
                <div className="segmented">
                  <button className={preview ? '' : 'on'} onClick={() => setPreview(false)}>
                    Write
                  </button>
                  <button className={preview ? 'on' : ''} onClick={() => setPreview(true)}>
                    Preview
                  </button>
                </div>
              </div>
              {preview ? (
                <div
                  className="news-preview"
                  dangerouslySetInnerHTML={{ __html: miniMarkdown(draft.body) }}
                />
              ) : (
                <textarea
                  className="news-body"
                  value={draft.body}
                  placeholder={'## What changed\n\n- Something useful\n- Something else'}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
              )}
            </div>

            <div className="news-actions">
              {!creating && (
                <button className="ghost danger" onClick={remove}>Delete</button>
              )}
              <div className="spacer" />
              {draft.updatedBy && (
                <span className="hint">Last edited by {draft.updatedBy}</span>
              )}
              <button disabled={busy || !draft.title} onClick={() => save('draft')}>
                Save draft
              </button>
              <button
                className="primary"
                disabled={busy || !draft.title}
                onClick={() => save('published')}
              >
                {draft.state === 'published' ? 'Update live post' : 'Publish'}
              </button>
            </div>
          </div>
        )}
      </main>

      {dialog}
    </>
  );
}
